/**
 * Applicant "voice" — whether the fact-find addresses the person seeking advice directly
 * ("your home", "do you have…") or refers to them by name ("Bob & Sally's home", "do Bob & Sally
 * have…").
 *
 * Step 1 asks who is completing the form. Whenever that is someone other than the applicant — a
 * family member, an attorney, an adviser — second-person labels are ambiguous at best and wrong at
 * worst: "who will stay in your home?" reads as the *completer's* home. Every label that refers to
 * the applicant's own circumstances goes through this helper instead.
 *
 * Text that addresses the *reader* rather than the applicant ("if you're not sure, leave it blank",
 * "you can change this at any time") is correct in second person for both voices and is left alone.
 *
 * Falls back to second person until the primary applicant's first name is known, so a half-filled
 * Step 2 never renders "'s Home".
 */
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';

export interface ApplicantVoice {
    /** True when labels should name the applicant instead of saying "you". */
    isThirdPerson: boolean;
    /** Subject: `you` | `Bob` | `Bob & Sally`. Also serves as the object ("for you" / "for Bob"). */
    subject: string;
    /** `subject` at the start of a sentence: `You` | `Bob` | `Bob & Sally`. */
    Subject: string;
    /** Possessive naming the applicant: `your` | `Bob's` | `Bob & Sally's`. */
    possessive: string;
    /** `possessive` at the start of a sentence or in a heading: `Your` | `Bob's` | `Bob & Sally's`. */
    Possessive: string;
    /**
     * Pronoun for a repeat reference inside the same sentence, where naming again would grate:
     * `you` | `they`. Always takes the plural verb form ("you have" / "they have").
     */
    pronoun: string;
    /** Possessive pronoun to match `pronoun`: `your` | `their`. */
    pronounPossessive: string;
    /**
     * Verb agreeing with `subject` — `verb('have', 'has')`. "you"/a couple take the plural form,
     * a single named applicant the singular one. Never use with `pronoun`, which is always plural.
     */
    verb: (plural: string, singular: string) => string;
}

/** The slice of the fact-find that decides the voice. */
type VoiceSource = Pick<FactFind, 'completer' | 'adviceFor' | 'client' | 'partner'>;

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export const buildApplicantVoice = (factFind: VoiceSource | undefined): ApplicantVoice => {
    const completedByApplicant = (factFind?.completer || 'client') === 'client';
    const clientFirstName = (factFind?.client?.firstName || '').trim();
    const partnerFirstName = (factFind?.partner?.firstName || '').trim();
    const isPartnered = factFind?.adviceFor === 'couple';

    const names = [clientFirstName, isPartnered ? partnerFirstName : ''].filter(Boolean);
    /**
     * Answering "I'm a family member" is enough — the name is NOT required.
     *
     * Third person used to wait for the client's first name, which is collected on step 2. So a
     * daughter filling this in for her mother reached a step headed "Your Details", above a
     * fieldset headed "Primary Applicant Details", one screen after giving HER OWN details under
     * "Your Details (as the person completing this form)" — the same words for two different
     * people. The headings then changed under her as she typed her mother's name. Reported
     * 2026-08-20.
     *
     * Until a name arrives the voice speaks in the third person without one, which is the whole
     * point: it is already not about the reader.
     */
    const isThirdPerson = !completedByApplicant;

    // "Bob & Sally" — the couple's own preferred order is Step 2's client/partner order.
    const subject = isThirdPerson ? names.join(' & ') || "the person you're helping" : 'you';
    // Possessive sits on the last name only: "Bob & Sally's". Always "'s", including for names
    // already ending in s ("James's"), which is current Australian usage. With no name yet,
    // "their" — never "the person you're helping's".
    const possessive = isThirdPerson ? (names.length ? `${subject}'s` : 'their') : 'your';
    const isPlural = !isThirdPerson || names.length > 1;

    return {
        isThirdPerson,
        subject,
        Subject: capitalise(subject),
        possessive,
        Possessive: capitalise(possessive),
        pronoun: isThirdPerson ? 'they' : 'you',
        pronounPossessive: isThirdPerson ? 'their' : 'your',
        verb: (plural, singular) => (isPlural ? plural : singular),
    };
};
